import { useMemo } from 'react';

import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';

import { ROUTE_KEYS } from './constants';
import { RouteKey } from './navigationUtils';

export function useCanAccessPage(page: RouteKey) {
  const canAccessTasksPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_TASKS_PAGE,
    { detailed: false }
  );
  const canAccessCollaboratorsPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_COLLABORATORS_PAGE,
    { detailed: false }
  );
  const canAccessUserAdminPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_USER_ADMIN_PAGE,
    { detailed: false }
  );
  const canAccessTeamsAdminPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_TEAMS_ADMIN_PAGE,
    { detailed: false }
  );
  const canAccessBrandingAdminPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_BRANDING_ADMIN_PAGE,
    { detailed: false }
  );
  const canAccessSubBrandsAdminPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_SUB_BRANDS_ADMIN_PAGE,
    { detailed: false }
  );
  const canAccessIntegrationsAdminPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_INTEGRATIONS_ADMIN_PAGE,
    { detailed: false }
  );
  const canAccessInternalTools = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_INTERNAL_TOOLS,
    { detailed: false }
  );
  const canAccessLegalDisclaimersPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_LEGAL_DISCLAIMERS_PAGE,
    { detailed: false }
  );
  const hasAdminCategoriesPageAccess = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_ADMIN_CATEGORIES_PAGES,
    { detailed: false }
  );
  const hasAuditLogPageAccess = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_AUDIT_LOG_PAGE,
    { detailed: false }
  );
  const canAccessRolesAdminPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_ROLES_ADMIN_PAGE,
    { detailed: false }
  );
  const canAccessSecurityAdminPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_SECURITY_ADMIN_PAGE,
    { detailed: false }
  );
  const canAccessManagedSFTP = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_MANAGE_SFTP,
    { detailed: false }
  );
  const canAccessSummaryPromptsAdminPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_SUMMARY_PROMPTS_ADMIN_PAGE,
    { detailed: false }
  );
  const isFormsExtractionEnabled = useFeatureFlag('forms_extraction');
  const canAccessFormsAdminPage =
    canAccessSummaryPromptsAdminPage && isFormsExtractionEnabled;

  const canAccessHouseholdAccessPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_HOUSEHOLD_ACCESS_PAGE,
    { detailed: false }
  );
  const hasReportingAdminPermission = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_REPORTING_ADMIN_PAGE,
    { detailed: false }
  );
  const isReportingAdminSectionEnabled = useFeatureFlag(
    'tenant_reporting_admin_section'
  );
  const canAccessReportingAdminPage =
    hasReportingAdminPermission && isReportingAdminSectionEnabled;

  const canAccessAnyAdminPage =
    canAccessUserAdminPage ||
    canAccessTeamsAdminPage ||
    canAccessBrandingAdminPage ||
    canAccessSubBrandsAdminPage ||
    canAccessIntegrationsAdminPage ||
    canAccessLegalDisclaimersPage ||
    hasAdminCategoriesPageAccess ||
    canAccessInternalTools ||
    hasAuditLogPageAccess ||
    canAccessRolesAdminPage ||
    canAccessSecurityAdminPage ||
    canAccessManagedSFTP ||
    canAccessSummaryPromptsAdminPage ||
    canAccessReportingAdminPage;

  const pageAccessLookup = useMemo<Partial<Record<RouteKey, boolean>>>(() => {
    return {
      [ROUTE_KEYS.TASKS]: canAccessTasksPage,
      [ROUTE_KEYS.COLLABORATORS]: canAccessCollaboratorsPage,
      [ROUTE_KEYS.ADMIN_USERS]: canAccessUserAdminPage,
      [ROUTE_KEYS.ADMIN_TEAMS]: canAccessTeamsAdminPage,
      [ROUTE_KEYS.ADMIN_BRANDING]: canAccessBrandingAdminPage,
      [ROUTE_KEYS.ADMIN_SUB_BRANDS]: canAccessSubBrandsAdminPage,
      [ROUTE_KEYS.ADMIN_INTEGRATIONS]:
        canAccessIntegrationsAdminPage || canAccessManagedSFTP,
      [ROUTE_KEYS.ADMIN_SFTP_FILES]: canAccessManagedSFTP,
      [ROUTE_KEYS.ADMIN_LEGAL_DISCLAIMERS]: canAccessLegalDisclaimersPage,
      [ROUTE_KEYS.ADMIN_CATEGORIES]: hasAdminCategoriesPageAccess,
      [ROUTE_KEYS.ADMIN]: canAccessAnyAdminPage,
      [ROUTE_KEYS.ADMIN_AUDIT_LOG]: hasAuditLogPageAccess,
      [ROUTE_KEYS.ADMIN_ROLES]: canAccessRolesAdminPage,
      [ROUTE_KEYS.ADMIN_SECURITY]: canAccessSecurityAdminPage,
      [ROUTE_KEYS.ADMIN_SUMMARY_PROMPTS]: canAccessSummaryPromptsAdminPage,
      [ROUTE_KEYS.ADMIN_PROMPT_ITERATION]: canAccessSummaryPromptsAdminPage,
      [ROUTE_KEYS.ADMIN_FORMS]: canAccessFormsAdminPage,
      [ROUTE_KEYS.ADMIN_DOCUMENT_SUMMARIES_REPORT]: canAccessReportingAdminPage,
      [ROUTE_KEYS.HOUSEHOLD_FORM_SUBMISSIONS]: isFormsExtractionEnabled,
      [ROUTE_KEYS.HOUSEHOLD_FORM_SUBMISSION_RESULT]: isFormsExtractionEnabled,
      [ROUTE_KEYS.HOUSEHOLD_SIDEBAR_ACCESS]: canAccessHouseholdAccessPage,
    };
  }, [
    canAccessTasksPage,
    canAccessCollaboratorsPage,
    canAccessUserAdminPage,
    canAccessTeamsAdminPage,
    canAccessBrandingAdminPage,
    canAccessSubBrandsAdminPage,
    canAccessIntegrationsAdminPage,
    canAccessLegalDisclaimersPage,
    hasAdminCategoriesPageAccess,
    canAccessAnyAdminPage,
    hasAuditLogPageAccess,
    canAccessRolesAdminPage,
    canAccessSecurityAdminPage,
    canAccessManagedSFTP,
    canAccessSummaryPromptsAdminPage,
    canAccessFormsAdminPage,
    canAccessReportingAdminPage,
    isFormsExtractionEnabled,
    canAccessHouseholdAccessPage,
  ]);

  return pageAccessLookup[page];
}
